import type { TDeferredRefetchQueryKey } from "@frontend-shared/utils";
import { createDeferredRefetchScheduler } from "@frontend-shared/utils";

import {
  PUSH_REFETCH_DEBOUNCE_MS,
  PUSH_REFETCH_MAX_WAIT_MS,
} from "@/constants/websocket";

import { queryClient } from "./query-client";

// NOTE: NSAI-5911 — the web binding of the shared deferred-refetch scheduler
// (frontend-shared/utils/deferred-refetch-scheduler.ts). Socket pushes used
// to `invalidateQueries` on every event, refetching at once even in a hidden
// tab — unlike `refetchInterval` polling, which React Query's focusManager
// already pauses. Every push-driven refetch now goes through here: the query
// is marked stale at once (so a remount picks it up), and refetched once per
// burst while the tab is visible, or once on `visibilitychange` → visible.
// The app sets `refetchOnWindowFocus: false` globally, which is why the
// return-to-visible refetch has to be explicit.

const isDocumentHidden = (): boolean =>
  typeof document !== "undefined" && document.visibilityState === "hidden";

const pushRefetchScheduler = createDeferredRefetchScheduler({
  isHidden: isDocumentHidden,
  markStale: (queryKey) => {
    void queryClient.invalidateQueries({ queryKey, refetchType: "none" });
  },
  // NOTE: Every active match is refetched, as `invalidateQueries` did — no
  // `stale: true` filter: a cache write after the push (a frame on a
  // rejoined chat, an older response landing) clears the invalidated flag,
  // and for any query with a `staleTime` that filter would then skip the
  // catch-up for good. A fetch already in flight is cancelled and restarted
  // (React Query's default): it may have left before the pushed row existed,
  // and joining it would leave the badge / list one notification behind.
  refetch: (queryKey) => {
    void queryClient.refetchQueries({ queryKey, type: "active" });
  },
  debounceMs: PUSH_REFETCH_DEBOUNCE_MS,
  maxWaitMs: PUSH_REFETCH_MAX_WAIT_MS,
});

export const schedulePushRefetch = (
  queryKeys: readonly TDeferredRefetchQueryKey[],
): void => {
  pushRefetchScheduler.schedule({ queryKeys });
};

// NOTE: For a catch-up whose result must land BEFORE anything the socket
// delivers next — the chat-history refetch on (re)connect. A debounced
// refetch there would land after the first streamed frames and overwrite
// them with an older snapshot, so a visible tab still refetches at once;
// only a hidden tab defers (marked stale now, one refetch on return).
export const refetchNowOrWhenVisible = (
  queryKeys: readonly TDeferredRefetchQueryKey[],
): void => {
  if (isDocumentHidden()) {
    pushRefetchScheduler.schedule({ queryKeys });
    return;
  }
  for (const queryKey of queryKeys) {
    void queryClient.refetchQueries({ queryKey, type: "active" });
  }
};

// NOTE: Logout / teardown — the pending keys stay stale in the cache, so
// nothing is lost if the same queries mount again.
export const resetPushRefetchScheduler = (): void => {
  pushRefetchScheduler.reset();
};

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    pushRefetchScheduler.handleVisible();
  });
}
